/**
 * ShopLogin.tsx — Customer portal login page for the online shop
 * URL: /shop/:businessId/login
 */
import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';

const ShopLogin: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shop-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); return; }
      localStorage.setItem(`shopToken_${businessId}`, data.token);
      localStorage.setItem(`shopCustomer_${businessId}`, JSON.stringify(data.customer));
      navigate(`/shop/${businessId}`);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '24px 16px' }}>
      {/* Top bar back link */}
      <div style={{ width: '100%', maxWidth: 420, marginBottom: 12 }}>
        <Link to={`/shop/${businessId}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Back to shop
        </Link>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 420, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🛍️</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Welcome back</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>Sign in to your customer account</p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ padding: '13px 0', borderRadius: 9, border: 'none', background: loading ? '#93c5fd' : '#1e3a8a', color: '#fff', fontWeight: 800, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
          <Link to={`/shop/${businessId}/forgot-password`} style={{ color: '#6b7280', textDecoration: 'none' }}>
            Forgot your password?
          </Link>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: '#6b7280' }}>
          Don't have an account?{' '}
          <Link to={`/shop/${businessId}/register`} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>
            Register here
          </Link>
        </div>
      </div>

      <p style={{ marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
        Your data is safe and secure 🔒
      </p>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 8,
  border: '1.5px solid #e5e7eb',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fafafa',
};

export default ShopLogin;
